import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Button,
  Avatar,
  Tooltip,
  LinearProgress,
  Paper,
  IconButton,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  Menu,
  MenuItem,
  Alert,
} from "@mui/material";
import {
  AccountTree as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as DoneIcon,
  RadioButtonUnchecked as TodoIcon,
  PlayCircle as InProgressIcon,
  Lock as LockedIcon,
  LockOpen as UnlockedIcon,
  ArrowForward as ArrowIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  FlagOutlined as FlagIcon,
  Warning as WarningIcon,
  MoreVert as MoreVertIcon,
  SwapVert as ReorderIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import {
  getTaskGroups,
  getTaskGroupById,
  updateTaskGroup,
  deleteTaskGroup,
  reorderGroupTasks,
} from "services/company/projectManagement/task/taskService";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  todo: {
    label: "To Do",
    color: "#6366f1",
    bg: "rgba(99,102,241,0.1)",
    Icon: TodoIcon,
  },
  in_progress: {
    label: "In Progress",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.1)",
    Icon: InProgressIcon,
  },
  paused: {
    label: "Paused",
    color: "#64748b",
    bg: "rgba(100,116,139,0.1)",
    Icon: ScheduleIcon,
  },
  done: {
    label: "Done",
    color: "#10b981",
    bg: "rgba(16,185,129,0.1)",
    Icon: DoneIcon,
  },
  blocked: {
    label: "Blocked",
    color: "#ef4444",
    bg: "rgba(239,68,68,0.1)",
    Icon: WarningIcon,
  },
  review: {
    label: "Review",
    color: "#8b5cf6",
    bg: "rgba(139,92,246,0.1)",
    Icon: FlagIcon,
  },
};

const PRIORITY_CONFIG = {
  urgent: { label: "Urgent", color: "#ef4444" },
  high: { label: "High", color: "#f97316" },
  medium: { label: "Medium", color: "#f59e0b" },
  low: { label: "Low", color: "#10b981" },
};

const getStatusCfg = (s) => STATUS_CONFIG[s] || STATUS_CONFIG.todo;
const getPriorityCfg = (p) => PRIORITY_CONFIG[p] || PRIORITY_CONFIG.medium;

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function isOverdue(dueDate, status) {
  if (!dueDate || status === "done") return false;
  return new Date(dueDate) < new Date();
}

// ─── Task Chain Node (view mode) ─────────────────────────────────────────────

const TaskChainNode = ({ task, index, isLast, isLocked }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const cfg = getStatusCfg(task.status);
  const pCfg = getPriorityCfg(task.priority);
  const overdue = isOverdue(task.due_date, task.status);
  const StatusIcon = cfg.Icon;

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
      {/* Node Card */}
      <Box sx={{ flex: "0 0 210px", width: 210 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `2px solid ${
              isLocked ? "rgba(239,68,68,0.35)" : cfg.color + "55"
            }`,
            bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#fff",
            overflow: "hidden",
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 8px 24px ${cfg.color}28`,
              borderColor: cfg.color,
            },
          }}
        >
          <Box sx={{ height: 4, bgcolor: cfg.color }} />
          <Box sx={{ p: 1.8 }}>
            {/* Status row */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Chip
                icon={
                  <StatusIcon
                    sx={{ fontSize: 13, color: `${cfg.color} !important` }}
                  />
                }
                label={cfg.label}
                size="small"
                sx={{
                  bgcolor: cfg.bg,
                  color: cfg.color,
                  fontWeight: 700,
                  fontSize: 11,
                  height: 22,
                }}
              />
              {isLocked && (
                <Tooltip title="Waiting for previous task to complete">
                  <LockedIcon sx={{ fontSize: 15, color: "#ef4444" }} />
                </Tooltip>
              )}
              {!isLocked && index > 0 && (
                <Tooltip title="Unlocked – predecessor done">
                  <UnlockedIcon sx={{ fontSize: 15, color: "#10b981" }} />
                </Tooltip>
              )}
            </Stack>

            {/* Step number + title */}
            <Stack direction="row" alignItems="flex-start" spacing={1} mb={1}>
              <Avatar
                sx={{
                  width: 20,
                  height: 20,
                  fontSize: 10,
                  fontWeight: 800,
                  bgcolor: cfg.color,
                  color: "#fff",
                  flexShrink: 0,
                  mt: "1px",
                }}
              >
                {index + 1}
              </Avatar>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: "text.primary",
                  fontSize: 12,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {task.title}
              </Typography>
            </Stack>

            {/* Due */}
            <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
              <ScheduleIcon
                sx={{
                  fontSize: 11,
                  color: overdue ? "#ef4444" : "text.disabled",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: overdue ? "#ef4444" : "text.secondary",
                  fontWeight: overdue ? 700 : 400,
                  fontSize: 11,
                }}
              >
                {overdue ? "Overdue · " : ""}
                {formatDate(task.due_date)}
              </Typography>
            </Stack>

            {/* Assignee */}
            {task.assigned_to_name && (
              <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                <PersonIcon sx={{ fontSize: 11, color: "text.disabled" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: 11 }}
                  noWrap
                >
                  {task.assigned_to_name}
                </Typography>
              </Stack>
            )}

            {/* Priority */}
            <Box
              sx={{
                mt: 1.2,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: `${pCfg.color}18`,
                px: 1,
                py: 0.3,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: pCfg.color,
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: pCfg.color, fontWeight: 700, fontSize: 10 }}
              >
                {pCfg.label}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Connector */}
      {!isLast && (
        <Box
          sx={{
            flex: "0 0 44px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pt: "34px",
          }}
        >
          <Stack direction="row" alignItems="center">
            <Box
              sx={{
                height: 2,
                width: 16,
                bgcolor: isLocked ? "#ef444450" : "#10b98150",
                borderRadius: 1,
              }}
            />
            <ArrowIcon
              sx={{ fontSize: 16, color: isLocked ? "#ef4444" : "#10b981" }}
            />
          </Stack>
          <Typography
            variant="caption"
            sx={{ fontSize: 9, color: "text.disabled", mt: 0.2 }}
          >
            then
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Reorder Row (rearrange mode) ────────────────────────────────────────────

const ReorderRow = ({ task, index, total, onMoveUp, onMoveDown }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const cfg = getStatusCfg(task.status);

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.5,
        borderRadius: "12px",
        border: `1.5px solid ${cfg.color}40`,
        bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#fff",
        transition: "all 0.15s",
        "&:hover": {
          boxShadow: `0 4px 16px ${cfg.color}22`,
          borderColor: cfg.color,
        },
      }}
    >
      {/* Drag handle visual */}
      <DragIcon sx={{ color: "text.disabled", fontSize: 20, flexShrink: 0 }} />

      {/* Step badge */}
      <Avatar
        sx={{
          width: 28,
          height: 28,
          fontSize: 12,
          fontWeight: 800,
          bgcolor: cfg.color,
          color: "#fff",
          flexShrink: 0,
        }}
      >
        {index + 1}
      </Avatar>

      {/* Title + status */}
      <Box flex={1} minWidth={0}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: "text.primary" }}
          noWrap
        >
          {task.title}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} mt={0.3}>
          <Chip
            label={cfg.label}
            size="small"
            sx={{
              bgcolor: cfg.bg,
              color: cfg.color,
              fontWeight: 700,
              fontSize: 10,
              height: 18,
            }}
          />
          {task.assigned_to_name && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: 11 }}
              noWrap
            >
              {task.assigned_to_name}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Move buttons */}
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Move Up">
          <span>
            <IconButton
              size="small"
              disabled={index === 0}
              onClick={() => onMoveUp(index)}
              sx={{
                bgcolor: index === 0 ? "transparent" : "rgba(99,102,241,0.1)",
                color: index === 0 ? "text.disabled" : "#6366f1",
                "&:hover": { bgcolor: "rgba(99,102,241,0.2)" },
              }}
            >
              <MoveUpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move Down">
          <span>
            <IconButton
              size="small"
              disabled={index === total - 1}
              onClick={() => onMoveDown(index)}
              sx={{
                bgcolor:
                  index === total - 1 ? "transparent" : "rgba(99,102,241,0.1)",
                color: index === total - 1 ? "text.disabled" : "#6366f1",
                "&:hover": { bgcolor: "rgba(99,102,241,0.2)" },
              }}
            >
              <MoveDownIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
};

// ─── Group Card ───────────────────────────────────────────────────────────────

const GroupCard = ({ group, onEdit, onDelete }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [expanded, setExpanded] = useState(false);
  const [tasks, setTasks] = useState(null);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderedTasks, setReorderedTasks] = useState([]);
  const [saving, setSaving] = useState(false);

  const progress = Number(group.progress_percentage) || 0;
  const statusCfg = getStatusCfg(group.status);
  const overdue = isOverdue(group.due_date, group.status);

  const loadTasks = async () => {
    setLoadingTasks(true);
    try {
      const res = await getTaskGroupById({ task_group_id: group.id });
      const fetched = res.data?.tasks || [];
      setTasks(fetched);
      setReorderedTasks([...fetched]);
    } catch (e) {
      showErrorToast("Failed to load group tasks");
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleExpand = async () => {
    if (!expanded && tasks === null) await loadTasks();
    setExpanded((p) => !p);
    setReorderMode(false);
  };

  const getLockedState = (taskList, idx) => {
    if (idx === 0) return false;
    const prev = taskList[idx - 1];
    return prev && prev.status !== "done";
  };

  // ── Reorder helpers ──
  const moveTask = (index, direction) => {
    const arr = [...reorderedTasks];
    const swapIdx = direction === "up" ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= arr.length) return;
    [arr[index], arr[swapIdx]] = [arr[swapIdx], arr[index]];
    setReorderedTasks(arr);
  };

  const handleEnterReorder = () => {
    setReorderedTasks([...(tasks || [])]);
    setReorderMode(true);
  };

  const handleCancelReorder = () => {
    setReorderedTasks([...(tasks || [])]);
    setReorderMode(false);
  };

  const handleSaveReorder = async () => {
    setSaving(true);
    try {
      await reorderGroupTasks({
        task_group_id: group.id,
        task_ids: reorderedTasks.map((t) => t.id),
      });
      setTasks([...reorderedTasks]);
      showSuccessToast("Task order saved! Dependencies rebuilt.");
      setReorderMode(false);
    } catch (e) {
      showErrorToast("Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "18px",
        border: `1.5px solid ${
          isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
        }`,
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fafafa",
        overflow: "hidden",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 4px 32px rgba(0,0,0,0.08)" },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background: isDark
            ? "linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.06) 100%)"
            : "linear-gradient(135deg, rgba(99,102,241,0.06) 0%, rgba(139,92,246,0.04) 100%)",
        }}
      >
        {/* Title row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            flex={1}
            minWidth={0}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                bgcolor: "#6366f1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <GroupIcon sx={{ fontSize: 18, color: "#fff" }} />
            </Box>
            <Box flex={1} minWidth={0}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: "1rem",
                  lineHeight: 1.2,
                  color: "text.primary",
                }}
                noWrap
              >
                {group.title}
              </Typography>
              {group.project_name && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: 11 }}
                >
                  {group.project_name}
                  {group.milestone_name ? ` · ${group.milestone_name}` : ""}
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            flexShrink={0}
          >
            <Chip
              label={statusCfg.label}
              size="small"
              sx={{
                bgcolor: statusCfg.bg,
                color: statusCfg.color,
                fontWeight: 700,
                fontSize: 11,
                height: 24,
              }}
            />
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onEdit(group);
                }}
              >
                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit Group
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  if (!expanded) handleExpand();
                  else {
                    setReorderMode(true);
                    setReorderedTasks([...(tasks || [])]);
                  }
                }}
              >
                <ReorderIcon fontSize="small" sx={{ mr: 1 }} /> Rearrange Tasks
              </MenuItem>
              <Divider />
              <MenuItem
                sx={{ color: "error.main" }}
                onClick={() => {
                  setMenuAnchor(null);
                  onDelete(group);
                }}
              >
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete Group
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>

        {/* Stats */}
        <Stack direction="row" spacing={3} mt={2} flexWrap="wrap" useFlexGap>
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                fontSize: 10,
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              Tasks
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 800, color: "text.primary" }}
            >
              {group.completed_tasks ?? 0} / {group.total_tasks ?? 0}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                fontSize: 10,
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              Due
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                color: overdue ? "#ef4444" : "text.primary",
              }}
            >
              {formatDate(group.due_date)}
            </Typography>
          </Box>
          {Number(group.total_estimated_hours) > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.disabled",
                  fontSize: 10,
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                Hours
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, color: "text.primary" }}
              >
                {group.total_actual_hours}h / {group.total_estimated_hours}h
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Progress */}
        <Box mt={2}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: 11 }}
            >
              Progress
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 800, color: "#6366f1" }}
            >
              {progress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb",
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
                background:
                  progress === 100
                    ? "linear-gradient(90deg,#10b981,#34d399)"
                    : "linear-gradient(90deg,#6366f1,#8b5cf6)",
              },
            }}
          />
        </Box>

        {/* Expand toggle */}
        <Button
          size="small"
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={handleExpand}
          sx={{
            mt: 1.5,
            textTransform: "none",
            color: "#6366f1",
            fontWeight: 700,
            fontSize: 12,
            p: 0,
            "&:hover": { bgcolor: "transparent", opacity: 0.8 },
          }}
        >
          {expanded ? "Hide task chain" : "View task chain"}
        </Button>
      </Box>

      {/* ── Task Chain / Rearrange ── */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 3 }}>
          {loadingTasks ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} sx={{ color: "#6366f1" }} />
            </Box>
          ) : tasks && tasks.length > 0 ? (
            <>
              {/* ── Toolbar ── */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: 10,
                    letterSpacing: 1,
                  }}
                >
                  {reorderMode
                    ? "✦ Rearrange Mode — Drag or use arrows to set execution order"
                    : "Execution Pipeline — tasks run in sequence"}
                </Typography>
                {!reorderMode ? (
                  <Tooltip title="Rearrange the task execution order">
                    <Button
                      size="small"
                      startIcon={<ReorderIcon />}
                      onClick={handleEnterReorder}
                      variant="outlined"
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 12,
                        borderRadius: "8px",
                        borderColor: "#6366f1",
                        color: "#6366f1",
                        "&:hover": {
                          bgcolor: "rgba(99,102,241,0.08)",
                          borderColor: "#6366f1",
                        },
                      }}
                    >
                      Rearrange
                    </Button>
                  </Tooltip>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelReorder}
                      disabled={saving}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 12,
                        borderRadius: "8px",
                        color: "text.secondary",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      startIcon={
                        saving ? (
                          <CircularProgress size={14} sx={{ color: "#fff" }} />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      onClick={handleSaveReorder}
                      variant="contained"
                      disabled={saving}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 12,
                        borderRadius: "8px",
                        bgcolor: "#6366f1",
                        "&:hover": { bgcolor: "#4f46e5" },
                      }}
                    >
                      {saving ? "Saving…" : "Save Order"}
                    </Button>
                  </Stack>
                )}
              </Stack>

              {/* Rearrange mode info */}
              {reorderMode && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 2,
                    borderRadius: "10px",
                    fontSize: 12,
                    "& .MuiAlert-message": { fontSize: 12 },
                  }}
                >
                  Use the ↑ ↓ buttons to set which task runs first. When saved,
                  the dependency chain is rebuilt automatically.
                </Alert>
              )}

              {/* PIPELINE VIEW */}
              {!reorderMode && (
                <Box sx={{ overflowX: "auto", pb: 1 }}>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    sx={{ minWidth: "max-content" }}
                  >
                    {tasks.map((task, idx) => (
                      <TaskChainNode
                        key={task.id}
                        task={task}
                        index={idx}
                        isLast={idx === tasks.length - 1}
                        isLocked={getLockedState(tasks, idx)}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* REORDER VIEW */}
              {reorderMode && (
                <Stack spacing={1.5}>
                  {reorderedTasks.map((task, idx) => (
                    <ReorderRow
                      key={task.id}
                      task={task}
                      index={idx}
                      total={reorderedTasks.length}
                      onMoveUp={(i) => moveTask(i, "up")}
                      onMoveDown={(i) => moveTask(i, "down")}
                    />
                  ))}
                </Stack>
              )}
            </>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: "text.disabled", textAlign: "center", py: 2 }}
            >
              No tasks in this group yet. Add tasks from Calendar or Board view.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ─── Edit Dialog ──────────────────────────────────────────────────────────────

const EditGroupDialog = ({ open, group, onClose, onSaved }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (group) {
      setTitle(group.title || "");
      setDescription(group.description || "");
    }
  }, [group]);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await updateTaskGroup({
        task_group_id: group.id,
        title: title.trim(),
        description,
      });
      showSuccessToast("Group updated");
      onSaved();
      onClose();
    } catch (e) {
      showErrorToast("Failed to update group");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ fontWeight: 800, fontSize: "1.1rem" }}>
        Edit Task Group
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Group Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          variant="outlined"
          inputProps={{ maxLength: 100 }}
        />
        <TextField
          fullWidth
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          variant="outlined"
          multiline
          rows={3}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !title.trim()}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main View ────────────────────────────────────────────────────────────────

const TaskGroupsView = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editGroup, setEditGroup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTaskGroups();
      setGroups(res.data || []);
    } catch (e) {
      showErrorToast("Failed to load task groups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteTaskGroup({ task_group_id: deleteTarget.id });
      showSuccessToast("Group deleted");
      setDeleteTarget(null);
      fetchGroups();
    } catch (e) {
      showErrorToast("Failed to delete group");
    } finally {
      setDeleting(false);
    }
  };

  const totalTasks = groups.reduce((s, g) => s + Number(g.total_tasks || 0), 0);
  const completedTasks = groups.reduce(
    (s, g) => s + Number(g.completed_tasks || 0),
    0,
  );
  const overdueGroups = groups.filter((g) =>
    isOverdue(g.due_date, g.status),
  ).length;
  const doneGroups = groups.filter((g) => g.status === "done").length;

  return (
    <Box>
      {/* Stats banner */}
      {!loading && groups.length > 0 && (
        <Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
          {[
            { label: "Total Groups", value: groups.length, color: "#6366f1" },
            { label: "Completed Groups", value: doneGroups, color: "#10b981" },
            { label: "Overdue", value: overdueGroups, color: "#ef4444" },
            {
              label: "Tasks Done",
              value: `${completedTasks}/${totalTasks}`,
              color: "#f59e0b",
            },
          ].map((s) => (
            <Paper
              key={s.label}
              elevation={0}
              sx={{
                flex: "1 1 140px",
                minWidth: 140,
                borderRadius: "14px",
                border: `1.5px solid ${
                  isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"
                }`,
                bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff",
                px: 2.5,
                py: 1.8,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.disabled",
                  fontSize: 10,
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                {s.label}
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: 900, color: s.color, mt: 0.3 }}
              >
                {s.value}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Hint */}
      {!loading && groups.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            px: 2.5,
            py: 1.5,
            borderRadius: "12px",
            bgcolor: isDark ? "rgba(99,102,241,0.1)" : "rgba(99,102,241,0.06)",
            border: "1.5px solid rgba(99,102,241,0.2)",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "#6366f1", fontWeight: 600, fontSize: 12 }}
          >
            💡 Each group is a sequenced pipeline. Click{" "}
            <strong>View task chain</strong> to see the flow, then hit{" "}
            <strong>Rearrange</strong> to change the execution order.
          </Typography>
        </Paper>
      )}

      {/* Loading */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress sx={{ color: "#6366f1" }} />
            <Typography variant="body2" color="text.secondary">
              Loading task groups…
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Empty */}
      {!loading && groups.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            py: 8,
            px: 4,
            borderRadius: "18px",
            border: `2px dashed ${
              isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"
            }`,
            bgcolor: "transparent",
          }}
        >
          <GroupIcon
            sx={{ fontSize: 64, color: "#6366f1", mb: 2, opacity: 0.5 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            No Task Groups Yet
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 400, mx: "auto" }}
          >
            Select tasks in Calendar or Board view and click "Group &amp;
            Sequence" to create a sequenced pipeline.
          </Typography>
        </Paper>
      )}

      {/* Group list */}
      {!loading && groups.length > 0 && (
        <Stack spacing={2.5}>
          {groups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              onEdit={(g) => setEditGroup(g)}
              onDelete={(g) => setDeleteTarget(g)}
            />
          ))}
        </Stack>
      )}

      {/* Edit dialog */}
      <EditGroupDialog
        open={Boolean(editGroup)}
        group={editGroup}
        onClose={() => setEditGroup(null)}
        onSaved={fetchGroups}
      />

      {/* Delete confirm */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: "16px", maxWidth: 420 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "error.main" }}>
          Delete Group?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete{" "}
            <strong>{deleteTarget?.title}</strong>? All tasks will be unlinked
            but won't be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete Group"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskGroupsView;
